import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  getRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
} from "services/roleService"

// ── Async thunks ──────────────────────────────────────────────────────────────

export const fetchRoles = createAsyncThunk(
  "roles/fetchRoles",
  async ({ filter, pagination } = {}, { rejectWithValue }) => {
    try {
      const data = await getRoles(filter, pagination)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to fetch roles")
      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch roles")
    }
  }
)

export const fetchRoleById = createAsyncThunk(
  "roles/fetchRoleById",
  async (roleId, { rejectWithValue }) => {
    try {
      const data = await getRoleById(roleId)
      if (!data?.status) return rejectWithValue(data?.msg || "Role not found")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Role not found")
    }
  }
)

export const createRoleThunk = createAsyncThunk(
  "roles/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const data = await createRole(roleData)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to create role")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create role")
    }
  }
)

export const updateRoleThunk = createAsyncThunk(
  "roles/updateRole",
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const data = await updateRole(roleId, roleData)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to update role")
      return { roleId, ...data.data }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update role")
    }
  }
)

export const deleteRoleThunk = createAsyncThunk(
  "roles/deleteRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const data = await deleteRole(roleId)
      if (!data?.status) return rejectWithValue(data?.msg || "Failed to delete role")
      return roleId
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete role")
    }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  roles: [],
  selected: null,
  loading: false,
  error: null,
  totalCount: null,
  totalPages: null,
  currentPage: 1,
}

const roleSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    clearRoleError(state) {
      state.error = null
    },
    clearSelectedRole(state) {
      state.selected = null
    },
  },
  extraReducers: builder => {
    // fetchRoles
    builder
      .addCase(fetchRoles.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false
        state.roles = action.payload.data || []
        state.totalCount = action.payload.totalCount ?? null
        state.totalPages = action.payload.totalPages ?? null
        state.currentPage = action.payload.currentPage ?? 1
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // fetchRoleById
    builder
      .addCase(fetchRoleById.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.loading = false
        state.selected = action.payload
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // createRoleThunk
    builder
      .addCase(createRoleThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createRoleThunk.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) state.roles.push(action.payload)
      })
      .addCase(createRoleThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // updateRoleThunk
    builder
      .addCase(updateRoleThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateRoleThunk.fulfilled, (state, action) => {
        state.loading = false
        const { roleId, ...updated } = action.payload || {}
        state.roles = state.roles.map(role =>
          role._id === roleId ? { ...role, ...updated } : role
        )
        if (state.selected?._id === roleId) {
          state.selected = { ...state.selected, ...updated }
        }
      })
      .addCase(updateRoleThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // deleteRoleThunk
    builder
      .addCase(deleteRoleThunk.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteRoleThunk.fulfilled, (state, action) => {
        state.loading = false
        state.roles = state.roles.filter(role => role._id !== action.payload)
        if (state.totalCount !== null) state.totalCount -= 1
        if (state.selected?._id === action.payload) state.selected = null
      })
      .addCase(deleteRoleThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearRoleError, clearSelectedRole } = roleSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectAllRoles = state => state.RolesSlice?.roles ?? []
export const selectRolesLoading = state => state.RolesSlice?.loading ?? false
export const selectRolesError = state => state.RolesSlice?.error

export const makeSelectRoleById = () =>
  createSelector(
    selectAllRoles,
    (_, roleId) => roleId,
    (roles, roleId) => roles.find(r => r._id === roleId)
  )

export const selectSelectedRole = state => state.RolesSlice?.selected

export default roleSlice.reducer
